import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo.svg";
import { format } from "date-fns";

interface Booking {
  id: string;
  booking_datetime: string; // UTC ISO timestamp
  booking_timezone?: string;
  proposed_datetime?: string; // UTC ISO timestamp
  duration_minutes: number;
  price: number;
  status: string;
  coach_id: string | null;
  user_id: string;
}

const ProposalResponse = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", bookingId)
        .single();

      if (error) throw error;

      if (data.status !== "proposed" || !data.proposed_datetime) {
        toast({
          title: "Invalid Request",
          description: "This booking doesn't have a pending proposal.",
          variant: "destructive",
        });
        return;
      }

      setBooking(data);
    } catch (error) {
      console.error("Error fetching booking:", error);
      toast({
        title: "Error",
        description: "Could not load booking details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!booking) return;

    setProcessing(true);
    try {
      // Get coach profile if there's a coach assigned
      let coachName = undefined;
      if (booking.coach_id) {
        const { data: coachProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", booking.coach_id)
          .maybeSingle();
        coachName = coachProfile?.full_name || undefined;
      }

      // Update booking with proposed time as the new booking time
      const { error: updateError } = await supabase
        .from("bookings")
        .update({
          booking_datetime: booking.proposed_datetime, // Use proposed UTC datetime
          proposed_datetime: null,
          status: "confirmed",
        })
        .eq("id", booking.id);

      if (updateError) throw updateError;

      // Capture payment now that customer accepted
      const { error: captureError } = await supabase.functions.invoke('capture-payment', {
        body: { bookingId: booking.id }
      });

      if (captureError) {
        console.error('Payment capture error:', captureError);
        toast({
          title: "Payment Error",
          description: "Booking confirmed but payment capture failed. Please contact support.",
          variant: "destructive",
        });
      }

      // Fetch updated booking to get video_conference_link
      const { data: updatedBooking } = await supabase
        .from("bookings")
        .select("video_conference_link")
        .eq("id", booking.id)
        .single();

      // Send confirmation email (without proposed fields since they're removed)
      await supabase.functions.invoke("send-booking-notification", {
        body: {
          customerEmail: (await supabase.auth.getUser()).data.user?.email,
          customerName: "Student",
          bookingDatetime: booking.proposed_datetime, // Send UTC timestamp
          bookingTimezone: booking.booking_timezone,
          duration: booking.duration_minutes,
          price: booking.price,
          status: "confirmed",
          videoConferenceLink: updatedBooking?.video_conference_link,
          coachName,
        },
      });

      toast({
        title: "Proposal Accepted!",
        description: "Your lesson has been confirmed. You'll receive a reminder before the session.",
      });

      setTimeout(() => navigate("/booking"), 2000);
    } catch (error) {
      console.error("Error accepting proposal:", error);
      toast({
        title: "Error",
        description: "Could not accept the proposal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!booking) return;

    setProcessing(true);
    try {
      // Cancel the payment for declined proposals
      const { error: cancelError } = await supabase.functions.invoke('cancel-payment', {
        body: { bookingId: booking.id }
      });

      if (cancelError) {
        console.error('Payment cancellation error:', cancelError);
        toast({
          title: "Error",
          description: "Failed to cancel payment. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from("bookings")
        .update({
          proposed_datetime: null,
          status: "denied",
        })
        .eq("id", booking.id);

      if (error) throw error;

      toast({
        title: "Proposal Declined",
        description: "You can book a new time that works for you.",
      });

      setTimeout(() => navigate("/booking"), 2000);
    } catch (error) {
      console.error("Error declining proposal:", error);
      toast({
        title: "Error",
        description: "Could not decline the proposal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-accent/20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-accent/20 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Booking Not Found</CardTitle>
            <CardDescription>This booking request could not be found.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate("/")} className="w-full">
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Vocalzing logo" className="h-10 w-10" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Vocalzing
            </h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">New Time Proposed</CardTitle>
              <CardDescription>
                Your coach has suggested an alternative time for your lesson
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Original Request</h3>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{format(new Date(booking.booking_datetime), "MMMM d, yyyy")}</span>
                    <Clock className="w-4 h-4 ml-2" />
                    <span>{format(new Date(booking.booking_datetime), "h:mm a")}</span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium mb-2">Proposed New Time</h3>
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span>{format(new Date(booking.proposed_datetime!), "MMMM d, yyyy")}</span>
                    <Clock className="w-5 h-5 text-primary ml-2" />
                    <span>{format(new Date(booking.proposed_datetime!), "h:mm a")}</span>
                  </div>
                </div>

                <div className="bg-accent/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Duration</span>
                    <span className="font-medium">{booking.duration_minutes} minutes</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-muted-foreground">Price</span>
                    <span className="font-medium">${booking.price}</span>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3">
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1"
                disabled={processing}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Decline
              </Button>
              <Button
                onClick={handleAccept}
                className="flex-1"
                disabled={processing}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Accept New Time
              </Button>
            </CardFooter>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ProposalResponse;
